<script>
import TimeRangeInput from '@/components/general/time-range-input.vue'
import baseMixin from '@/mixins/baseMixin'
import { mapGetters } from 'vuex'
import { RateUnitType } from '@/shared/constants/booking/RateUnitType'
import { debounce, isEqual, isMatch } from 'lodash'
import generateBreakMinutesList, {
  generateDefaultBreakSelectionOption,
} from '@/utils/generate-break-minutes-list.js'
import { numericRegex } from '@/utils/regex.js'
import toast from '@/services/toasts/index.js'
import { ClientType } from '@/shared/constants/client/ClientType'
import ClientLoginLocationViewModel from '@/models/locations/clientLoginLocationViewModel'
import bookingMixin from '@/mixins/bookingMixin'
import { insertAfter } from '@/helpers/array-helpers'
import FormattedLocation from '../general/formatted-location.vue'
import LocationCertRequirementsCheckboxList from './location-cert-requirements-checkbox-list.vue'

const BREAK_MINUTE_LIST_INCREMENT_IN_MINS = 15

export default {
  name: 'BookingCreateForm',
  components: {
    TimeRangeInput,
    FormattedLocation,
    LocationCertRequirementsCheckboxList,
  },
  mixins: [baseMixin, bookingMixin],
  props: {
    selectedLocation: {
      type: ClientLoginLocationViewModel,
      required: true,
    },
    payload: {
      type: Object,
      required: true,
    },
    hideNotifyMeCheckbox: {
      type: Boolean,
    },
  },
  data: () => {
    return {
      formPayload: {},
      isBookingDatesValid: true,
      isBookingDetailsValid: true,
      multipleDaysCheckbox: false,
      notifyMeCheckbox: false,
      numberOfBookingsPerDate: 1,
      minNumBookingsPerDay: 1,
      minDurationMinutes: 0,
      earliestEndTime: null,
      rules: {},
      notes: '',
      selectedReason: null,
      selectedClassification: null,
      selectedGrades: [],
      selectedCertificates: [],
      selectedSubmittedBy: null,
      room: '',
      isFullDay: true,
      startTime: null,
      endTime: null,
      dates: [],
      selectedBreak: null,
      breaks: [],
      numericRegex,
      defaultBreakInMins: 0,
      showBreakDurationInput: false,
    }
  },
  computed: {
    ...mapGetters({
      grades: 'client/grades',
      getClassifications: 'client/classifications',
      getCerts: 'locations/certifications',
      bookingContacts: 'contacts/bookingContacts',
      isLoadingClassifications: 'client/isLoadingClassifications',
      isLoadingGrades: 'client/isLoadingGrades',
      isLoadingBookingContacts: 'contacts/isLoadingBookingContacts',
      isLoadingCertifications: 'locations/isLoadingCertifications',
      isLoadingCRUD: 'bookings/isLoadingCRUD',
      currentUser: 'auth/currentUser',
    }),
    selectedClassificationObject() {
      if (!this.selectedClassification) return null
      return this.classifications.find(
        (x) => x.id === this.selectedClassification
      )
    },
    isBelowMinBookingDuration() {
      if (!this.startTime || !this.endTime) return false
      return this.payload.templates[0].durationMinutes < this.minDurationMinutes
    },
    classifications() {
      return this.getClassifications(this.clientId)
    },
    certificates() {
      return this.getCerts(this.selectedLocation.locationId)
    },
    selectableCertificates() {
      // If a classification hasn't been selected, grab only location default
      // cert requirements (payClassId === null)
      if (!this.selectedClassification)
        return this.certificates.filter((cert) => cert.payClassId === null)

      // With a classification selected we'll need to grab the classification
      // specific certs as well as location default
      const classificationSpecificCerts = this.certificates.filter(
        (cert) =>
          cert.payClassId === null ||
          cert.payClassId === this.selectedClassification
      )

      const filteredClassificationSpecificCerts = []

      // If the location requirement and classification requirement certs exists
      // prefer the classification specific version
      for (const certificate of classificationSpecificCerts) {
        const duplicateCertIndex = filteredClassificationSpecificCerts.findIndex(
          (cert) => cert.id === certificate.id
        )

        // If not a duplicate, add to list
        if (duplicateCertIndex === -1) {
          filteredClassificationSpecificCerts.push(certificate)
          continue
        }

        // If duplicate cert is location default, remove from list
        // and replace with classification specific cert requirement
        if (certificate.payClassId === this.selectedClassification) {
          filteredClassificationSpecificCerts.splice(
            duplicateCertIndex,
            1,
            certificate
          )
        }
      }

      return filteredClassificationSpecificCerts
    },
    gradesList() {
      return this.grades(this.clientId)
    },
    contacts() {
      return this.bookingContacts(this.selectedLocation.locationId)
    },
    isValid() {
      return this.dates.length > 0 && this.isBookingDatesValid
    },
    clientId() {
      return this.selectedLocation.clientId
    },
    isUnitClassification() {
      return (
        this.selectedClassificationObject?.rateUnitType === RateUnitType.UNIT
      )
    },
    isHourlyClassification() {
      return (
        this.selectedClassificationObject?.rateUnitType === RateUnitType.HOURLY
      )
    },
    isHybridClassification() {
      return (
        this.selectedClassificationObject?.rateUnitType ===
        RateUnitType.CLIENT_UNIT_CANDIDATE_HOURLY
      )
    },
    getLocale() {
      return this.$i18n.locale
    },
    /**
     * Determines if the current logged in user or the currently selected location
     * is from the USA. Unlikely that selected location will be outside of user's
     * locale but wanted to cover the most bases.
     */
    isUSALoginOrLocation() {
      return this.getLocale === 'en-US'
    },
    isAUSLoginOrLocation() {
      return this.getLocale === 'en-AU'
    },
    isAusECOrTAClassification() {
      const ecAndTAClassificationList = [
        ClientType.PRIMARY,
        ClientType.SECONDARY,
        ClientType.EARLY_CHILDHOOD,
        ClientType.SEN,
        ClientType.P12,
        ClientType.TA,
        ClientType.KINDERGARTEN,
        ClientType.ECE,
      ]

      return (
        this.isAUSLoginOrLocation &&
        ecAndTAClassificationList.includes(this.selectedLocation?.clientTypeInt)
      )
    },
  },
  watch: {
    selectedClassification(value) {
      if (this.isHourlyClassification || this.isHybridClassification) {
        this.isFullDay = false
        this.initBreaksList()
      } else {
        this.isFullDay = true
        this.initBreaksForUnitBookings()
      }
    },
    selectedSubmittedBy(newVal) {
      if (!this.isLoadingBookingContacts && this.contacts.length > 0) {
        this.setContactsStandardGrades(newVal)
      }
    },
    notifyMeCheckbox() {
      this.$emit('toggleNotifyMe', this.notifyMeCheckbox)
    },
    startTime() {
      this.initBreaksList()
    },
    endTime() {
      this.initBreaksList()
    },
    isLoadingBookingContacts() {
      if (!this.isLoadingBookingContacts && this.contacts.length > 0) {
        this.initRoom()

        if (this.selectedSubmittedBy) {
          this.setContactsStandardGrades(this.selectedSubmittedBy)
        }
      }
    },
  },
  created() {
    this.initRules()
    this.initFormState()
  },
  methods: {
    initRules() {
      this.rules = {
        validRange: (v) => {
          const parsedV = parseInt(v)
          return (
            (!!parsedV &&
              parsedV >= this.minNumBookingsPerDay &&
              parsedV <= this.maxNumBookingsPerDay) ||
            this.$t('input.invalidRangeValidationText', {
              min: this.minNumBookingsPerDay,
              max: this.maxNumBookingsPerDay,
            })
          )
        },
        numeric: (v) =>
          this.numericRegex.test(v) ||
          this.$t('input.inputValidNumberValidationText'),
        required: (v) => !!v || this.$t('input.inputRequiredValidationText'),
      }
    },
    initRoom() {
      if (!this.selectedLocation.submittedByTemplateRequired) return
      const currentContact = this.contacts.find(
        (x) => x.id === this.selectedSubmittedBy
      )

      if (!currentContact) return
      this.room = currentContact.room
    },
    initFormState() {
      this.startTime = this.payload.templates[0].startTimeLocation
      this.endTime = this.payload.templates[0].endTimeLocation

      this.selectedReason = this.payload.templates[0].reason
      this.selectedSubmittedBy = this.payload.templates[0].submittedByOrReplacing

      this.selectedCertificates = this.payload.templates[0].certifications
      this.initRoom()
    },
    initBreaksForUnitBookings() {
      if (!this.isUnitClassification) return
      this.updateBreaks(this.selectedLocation.locationLunchBreakMinutes)
    },
    /**
     * Preselects the default break minutes option in the breaks select
     * input
     */
    setDefaultBreak() {
      const defaultBreak = generateDefaultBreakSelectionOption(
        this.breaks,
        this.defaultBreakInMins
      )

      if (defaultBreak.isNewOption) {
        // Insert the new break option into the breaks array
        this.breaks = insertAfter(
          this.breaks,
          defaultBreak.index,
          defaultBreak.option
        )
      }

      return defaultBreak.option
    },
    initBreaksList() {
      if (!this.showBreakDurationInput) return

      if (!this.startTime || !this.endTime) return

      this.breaks = generateBreakMinutesList(
        this.startTime,
        this.endTime,
        BREAK_MINUTE_LIST_INCREMENT_IN_MINS
      )

      this.selectedBreak = this.setDefaultBreak()
      this.updateBreaks(this.selectedBreak.id)
    },
    validateDatesForm() {
      return this.$refs.bookingDatesForm.validate()
    },
    updateTime(payload) {
      this.startTime = payload.start
      this.endTime = payload.end
      this.isBookingDatesValid = this.validateDatesForm()
      this.$emit('timeUpdated', {
        startTime: this.startTime,
        endTime: this.endTime,
        isValid: this.validateDatesForm(),
      })
    },
    updateGrades(selected) {
      this.selectedGrades = selected
      // Prevent formChanged flag if update isn't required
      if (isEqual(selected, this.payload.templates[0].grades)) return false

      this.validateDatesForm()
      this.$emit('gradesUpdated', this.selectedGrades)
    },
    updateCerts(selected) {
      this.selectedCertificates = selected

      // Prevent formChanged flag if update isn't required
      if (isEqual(selected, this.payload.templates[0].certifications))
        return false

      this.validateDatesForm()
      this.$emit('certsUpdated', this.selectedCertificates)
    },
    setTimeToDefault() {
      if (!this.isFullDay) return

      this.updateTime({
        start: this.selectedLocation.startTime,
        end: this.selectedLocation.endTime,
      })
    },
    async handleSelectedCertRequirementsOnClassificationUpdate() {
      let certsIdsToSelect = []
      const oldSelectedList = this.selectedCertificates

      if (!this.selectedClassification) {
        certsIdsToSelect = this.selectableCertificates
          .filter((cert) => cert.selected)
          .map((cert) => cert.id)
      } else {
        certsIdsToSelect = this.selectableCertificates
          .filter((cert) => cert.selected || cert.payClassId !== null)
          .map((cert) => cert.id)
      }

      if (!isMatch(oldSelectedList.sort(), certsIdsToSelect.sort())) {
        await this.updateCerts(certsIdsToSelect)
        toast.info(this.$t('bookingCreate.certificationSelectionChanged'))
      }
    },
    updateMultipleBookings(number) {
      this.numberOfBookingsPerDate = number
      this.validateDatesForm()
      this.$emit('changeMultipleBookings', number)
    },
    setMultipleBookingsPerDateToDefault() {
      if (!this.multipleDaysCheckbox) return

      this.updateMultipleBookings(1)
    },
    changeLocation() {
      this.$emit('changeLocation')
    },
    setBookingFormParametersOnClassificationSelect() {
      this.minDurationMinutes = this.selectedClassificationObject?.extraValidation?.minimumDurationInMins
      this.earliestEndTime = this.selectedClassificationObject?.extraValidation?.earliestEndTime24hr
      this.defaultBreakInMins = this.selectedClassificationObject?.extraValidation?.defaultBreakInMins
      this.showBreakDurationInput = this.selectedClassificationObject?.extraValidation?.shouldShowBreaks
    },
    async updateClassification(value) {
      this.setBookingFormParametersOnClassificationSelect()
      await this.$nextTick()
      this.validateDatesForm()
      this.$emit('classificationUpdated', value)
      await this.handleSelectedCertRequirementsOnClassificationUpdate()
    },
    updateReason(value) {
      this.validateDatesForm()
      this.$emit('reasonUpdated', value)
    },
    updateRoom(value) {
      this.validateDatesForm()
      this.$emit('roomUpdated', value)
    },
    updateSubmittedBy(value) {
      const submittedBy = this.contacts.find((x) => x.id === value)
      this.room = submittedBy.room
      this.updateRoom(submittedBy.room)
      this.$emit('submittedByUpdated', value)
    },
    updateNotes: debounce(function(value) {
      this.$emit('notesUpdated', value)
    }, 500),
    updateBreaks(value) {
      this.$emit('breaksUpdated', value)
    },
    setContactsStandardGrades(contactId) {
      if (!contactId || !this.contacts || this.contacts.length === 0) return

      const replacingContact = this.contacts.find(
        (contact) => contact?.id === contactId
      )

      this.selectedGrades =
        replacingContact?.standardBookings?.[0]?.subjectList ?? []

      if (isEqual(this.selectedGrades, this.payload.templates[0].grades)) return

      this.validateDatesForm()
      this.$emit('gradesUpdated', this.selectedGrades)
    },
  },
}
</script>

<template>
  <v-card :loading="isLoadingCRUD" class="custom-elevation rounded-xl">
    <v-card-text>
      <FormattedLocation
        :location="selectedLocation"
        @changeLocation="changeLocation"
      />

      <div>
        <v-form
          ref="bookingDatesForm"
          lazy-validation
          :value="isBookingDatesValid"
          autocomplete="off"
        >
          <div>
            <v-select
              id="create-booking-form__classification-select"
              ref="create-booking-form__classification-select"
              v-model="selectedClassification"
              :items="classifications"
              :label="$t('bookingCreate.formClassificationInputLabel')"
              item-text="onlineTitle"
              item-value="id"
              :loading="isLoadingClassifications"
              :disabled="isLoadingCRUD"
              :rules="[rules.required]"
              :hint="$t('input.inputRequiredValidationText')"
              persistent-hint
              @input="updateClassification"
            />

            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <div class="d-inline-block" v-on="on">
                  <v-checkbox
                    id="create-booking__multiple-bookings"
                    v-model="multipleDaysCheckbox"
                    class="mt-4"
                    color="info"
                    dense
                    :disabled="isLoadingCRUD"
                    @click="setMultipleBookingsPerDateToDefault"
                  >
                    <template v-slot:label>
                      <div>
                        {{
                          $t('bookingCreate.formMultipleBookingsCheckboxLabel')
                        }}
                        <v-icon small>mdi-help-circle</v-icon>
                      </div>
                    </template>
                  </v-checkbox>
                </div>
              </template>
              <span>
                {{ $t('bookingCreate.formMultipleBookingsCheckboxTooltip') }}
              </span>
            </v-tooltip>

            <v-expand-transition>
              <div v-if="multipleDaysCheckbox">
                <v-text-field
                  id="create-booking__multiple-bookings-input"
                  v-model="numberOfBookingsPerDate"
                  type="number"
                  onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  :label="$t('bookingCreate.formMultipleBookingsInputLabel')"
                  min="1"
                  max="100"
                  step="1"
                  :disabled="isLoadingCRUD"
                  :rules="[rules.validRange, rules.numeric]"
                  @input="updateMultipleBookings"
                />
              </div>
            </v-expand-transition>
          </div>

          <div>
            <v-alert
              v-if="isBelowMinBookingDuration"
              id="booking-create-form__below-min-duration-alert"
              type="error"
              text
            >
              {{
                $t('bookingCreate.validationMessageBelowMinDuration', {
                  minDurationMinutes: minDurationMinutes / 60,
                })
              }}
            </v-alert>

            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <div class="d-inline-block" v-on="on">
                  <v-checkbox
                    v-if="isUnitClassification"
                    id="create-booking__full-day-checkbox"
                    v-model="isFullDay"
                    dense
                    color="info"
                    :disabled="isLoadingCRUD"
                    @click="setTimeToDefault"
                  >
                    <template v-slot:label>
                      <div>
                        {{ $t('bookingCreate.formFullDayCheckboxLabel') }}
                        <v-icon small>mdi-help-circle</v-icon>
                      </div>
                    </template>
                  </v-checkbox>
                </div>
              </template>
              <span>
                {{ $t('bookingCreate.formFullDayCheckboxTooltip') }}
              </span>
            </v-tooltip>

            <TimeRangeInput
              :start="startTime"
              :end="endTime"
              :min-end-time="earliestEndTime"
              one-line
              :disabled="isLoadingCRUD || isFullDay"
              @timeUpdated="updateTime"
            />
          </div>

          <div v-if="showBreakDurationInput">
            <v-select
              id="create-booking-form__breaks-select"
              ref="create-booking-form__breaks-select"
              :label="$t('bookingCreate.formBreakInputLabel')"
              :value="selectedBreak"
              :items="breaks"
              item-text="text"
              item-value="id"
              :return-object="false"
              :disabled="isLoadingCRUD"
              @change="updateBreaks"
            />
          </div>
        </v-form>
      </div>

      <div>
        <v-form
          ref="bookingDetailsForm"
          lazy-validation
          :value="isBookingDetailsValid"
          autocomplete="off"
          :disabled="isLoadingCRUD"
        >
          <v-autocomplete
            id="create-booking-form__grade-select"
            ref="create-booking-form__grade-select"
            :label="$t('bookingCreate.formGradesInputLabel')"
            :value="selectedGrades"
            :items="gradesList"
            item-text="title"
            item-value="id"
            :multiple="true"
            :loading="isLoadingGrades"
            chips
            small-chips
            @input="updateGrades"
          ></v-autocomplete>

          <article v-if="selectedLocation.submittedByTemplateRequired">
            <v-select
              id="create-booking-form__submitted-by-select"
              ref="create-booking-form__submitted-by-select"
              v-model="selectedSubmittedBy"
              :items="contacts"
              :label="$t('bookingCreate.formReplacingInputLabel')"
              item-text="fullName"
              item-value="id"
              :loading="isLoadingBookingContacts"
              required
              @input="updateSubmittedBy"
            ></v-select>

            <v-text-field
              id="create-booking-form__room-input"
              v-model="room"
              :label="$t('bookingCreate.formRoomInputLabel')"
              @input="updateRoom"
            />
          </article>

          <v-select
            id="create-booking-form__reason-select"
            ref="create-booking-form__reason-select"
            v-model="selectedReason"
            :items="reasonsForAbsence"
            :label="$t('bookingCreate.formReasonInputLabel')"
            item-text="name"
            item-value="id"
            @input="updateReason"
          ></v-select>

          <v-textarea
            id="create-booking-form__notes-input"
            ref="create-booking-form__notes-input"
            name="notes"
            :label="$t('bookingCreate.formNotesInputLabel')"
            :value="notes"
            auto-grow
            rows="1"
            @input="updateNotes"
          ></v-textarea>

          <v-checkbox
            v-if="!hideNotifyMeCheckbox"
            id="create-booking-form__notify-me-checkbox"
            v-model="notifyMeCheckbox"
            color="info"
            :loading="isLoadingBookingContacts"
            dense
            :label="$t('bookingCreate.formNotifyMeCheckboxLabel')"
          >
          </v-checkbox>

          <LocationCertRequirementsCheckboxList
            :loading="isLoadingCertifications"
            :items="selectableCertificates"
            :preselect="selectedCertificates"
            @updated="updateCerts"
          />
        </v-form>
      </div>
    </v-card-text>
  </v-card>
</template>
